// doctor-frontend/src/clinical/visitContextEngine.js
// Converts service context + atomic chief complaints into one primary clinical pathway
// plus reusable modules. Pathway selection and module activation are deliberately separate.

import { findComplaintDefinition, normalizeChiefComplaintRows } from './visitComplaintRegistry';

const SERVICE_DRIVEN_PATHWAYS = {
  'Antenatal Care': 'ANC Registration / Follow-up',
  'Postnatal Care': 'Postnatal Mother Review',
  'Newborn / Young Infant Care': 'Sick Newborn / Young Infant Pathway',
  'Under-5 / Child Health': 'IMNCI / Sick Child Pathway',
  'Adolescent Health': 'Adolescent OPD Pathway',
  'Geriatric Care': 'General Geriatric Pathway',
  'NCD Screening / Follow-up': 'Combined NCD Follow-up',
  'Occupational Health': 'Occupational Exposure Review',
  'Minor Procedure / Counseling': 'Procedure / Counseling Pathway',
  'LGBTQIA+ Health / Counseling': 'General Health Assessment Pathway',
};

const SERVICE_MODULES = {
  'Antenatal Care': ['maternal', 'nutrition'],
  'Postnatal Care': ['maternal', 'newborn'],
  'Newborn / Young Infant Care': ['newborn', 'paediatric'],
  'Under-5 / Child Health': ['paediatric', 'nutrition'],
  'Adolescent Health': ['adolescent'],
  'Geriatric Care': ['geriatric', 'ncd'],
  'NCD Screening / Follow-up': ['ncd'],
  'Occupational Health': ['occupational'],
  'Minor Procedure / Counseling': ['procedure', 'counseling'],
  'LGBTQIA+ Health / Counseling': ['counseling'],
};

export function derivePrimaryClinicalContext({
  serviceArea = 'General OPD',
  chiefComplaints = [],
  clinicalPathway = '',
  triagePriority = '',
} = {}) {
  const rows = normalizeChiefComplaintRows(chiefComplaints);
  const primaryRow = rows[0] || null;
  const primaryDef = primaryRow ? findComplaintDefinition(primaryRow.complaintCode || primaryRow.complaint) : null;
  const serviceDriven = SERVICE_DRIVEN_PATHWAYS[serviceArea] || '';

  // For service-driven care, service context establishes the pathway. For General OPD
  // and Adult Medicine, the first ordered chief complaint normally determines it.
  const complaintDrivenAreas = new Set(['General OPD', 'Adult Medicine']);
  const derivedPathway = complaintDrivenAreas.has(serviceArea)
    ? (primaryDef?.defaultPathway || clinicalPathway || 'General Symptom Assessment')
    : (serviceDriven || primaryDef?.defaultPathway || clinicalPathway || 'General Symptom Assessment');

  const complaintGroup = primaryDef?.complaintGroup || (primaryRow?.complaint || 'Other General OPD');
  const primaryPresentingProblem = primaryRow?.complaint || complaintGroup;

  const modules = new Set(SERVICE_MODULES[serviceArea] || []);
  rows.forEach((row) => {
    const def = findComplaintDefinition(row.complaintCode || row.complaint);
    (def?.modules || []).forEach((module) => modules.add(module));
  });
  if (['priority', 'emergency'].includes(String(triagePriority || '').toLowerCase())) {
    modules.add('red-flag');
  }

  return {
    clinicalPathway: derivedPathway,
    clinicalFocus: derivedPathway,
    complaintGroup,
    primaryPresentingProblem,
    activeClinicalModules: Array.from(modules),
  };
}

function durationText(row) {
  const value = row?.durationValue;
  const unit = String(row?.durationUnit || '').trim();
  if (value === '' || value == null || !unit) return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  const singular = unit.endsWith('s') ? unit.slice(0, -1) : unit;
  return `${n} ${n === 1 ? singular : unit}`;
}

export function formatChiefComplaintSummary(chiefComplaints = [], additionalText = '') {
  const rows = normalizeChiefComplaintRows(chiefComplaints);
  const parts = rows.map((row) => {
    const duration = durationText(row);
    return duration ? `${row.complaint} — ${duration}` : row.complaint;
  });
  const extra = String(additionalText || '').trim();
  if (extra) parts.push(extra);
  return parts.join('; ');
}
